import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Produktkarte für die Sortiment-Seite
 * Zeigt Produktbild, Name, Beschreibung, Allergene und ausklappbare Zutaten
 */
export const ProductCard = ({ product, priority = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden h-full">
      <div className="flex flex-col sm:flex-row h-full">
        {/* Produktbild - Links */}
        <div className="sm:w-2/5 flex-shrink-0">
          <div className="relative h-40 sm:h-full min-h-[140px] bg-[#f0ebe6]">
            {/* Blur Placeholder während Laden */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-[#f0ebe6] animate-pulse" />
            )}
            <img
              src={product.image || "/images/products/default-bread.webp"}
              alt={`${product.name} - Frisches Gebäck von Cafe Konditorei Rieppel Wien`}
              className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading={priority ? "eager" : "lazy"}
              decoding={priority ? "sync" : "async"}
              fetchPriority={priority ? "high" : "auto"}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.src = "/images/products/default-bread.webp";
                setImageLoaded(true);
              }}
            />
            {/* Verfügbare Größen Badge */}
            {product.available_sizes && (
              <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                {product.available_sizes.map((size, idx) => (
                  <span key={idx} className="bg-[#fad24b] text-[#1e1919] text-[10px] font-medium px-1.5 py-0.5 rounded-full shadow-sm">
                    {size}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Produktdetails - Rechts */}
        <div className="sm:w-3/5 p-3 sm:p-4 flex flex-col justify-between">
          {/* Header */}
          <div>
            <h3 className="font-bold text-[#1e1919] text-base sm:text-lg mb-0.5 leading-tight">
              {product.name}
            </h3>
            {product.alias && (
              <p className="text-[#61525a] text-xs mb-1">auch bekannt als: {product.alias}</p>
            )}

            {/* Description */}
            <p className="text-[#736c64] text-xs sm:text-sm mb-2 leading-relaxed">
              {product.description}
            </p>

            {/* Allergens */}
            <div className="flex flex-wrap items-center gap-1 mb-2">
              <span className="text-[#61525a] text-xs font-medium mr-1">Allergene:</span>
              {product.allergens.map((allergen, idx) => (
                <span key={idx} className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                  {allergen}
                </span>
              ))}
            </div>
          </div>

          {/* Expandable Ingredients */}
          <div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-[#61525a] text-xs font-medium hover:text-[#4a3f45] transition-colors"
              data-testid={`ingredients-toggle-${product.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Zutaten {isExpanded ? 'ausblenden' : 'anzeigen'}
            </button>

            {isExpanded && (
              <div className="mt-2 pt-2 border-t border-[#e5e2df]">
                <p className="text-[#736c64] text-[10px] sm:text-xs leading-relaxed">
                  {product.ingredients.join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
